import React, { useRef, useCallback } from 'react';
import UploadWidget, { EventResult, InitializedUploadWidget } from '../CloudinaryUploadWidget';
import { EventError } from '../CloudinaryUploadWidget/types';
export interface PhotoUploadProps {
  onUploadSuccess: (url: string) => void;
  onUploadError?: (error: Error) => void;
  onUploadCancel?: () => void;
  initialUrl?: string | null;
  isProcessing?: boolean;
  isUploading?: boolean;
  processingText?: string;
  onUploadClick?: () => void;
  children?: React.ReactNode | ((props: PhotoUploadRenderProps) => React.ReactNode);
}
export interface PhotoUploadRenderProps {
  currentImageUrl: string | null;
  isButtonDisabled: boolean;
  buttonText: string;
  handleUploadClick: () => void;
  discardStagedUpload: () => void;
  hasImage: boolean;
}
export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  onUploadSuccess,
  onUploadError,
  onUploadCancel,
  initialUrl,
  isProcessing = false,
  isUploading = false,
  processingText = 'Loading...',
  onUploadClick,
  children
}) => {
  const [uploadedImageUrl, setUploadedImageUrl] = React.useState<string | null>(initialUrl || null);
  const widgetRef = useRef<InitializedUploadWidget | null>(null);
  React.useEffect(() => {
    setUploadedImageUrl(initialUrl || null);
  }, [initialUrl]);
  const currentImageUrl = uploadedImageUrl || initialUrl;
  const hasImage = !!currentImageUrl;
  const handleWidgetLoad = (widget: InitializedUploadWidget) => {
    widgetRef.current = widget;
  };
  const handleUploadResult = (result: EventResult) => {
    if (result.event === 'success') {
      setUploadedImageUrl(result.info.secure_url);
      onUploadSuccess(result.info.secure_url);
    } else if (result.event === 'close') {
      onUploadCancel?.();
    }
  };
  const handleUploadError = (error: EventError) => {
    onUploadError?.(new Error(error.status || 'Upload failed'));
  };
  const discardStagedUpload = useCallback(() => {
    setUploadedImageUrl(initialUrl || null);
  }, [initialUrl]);
  const handleUploadClick = useCallback(() => {
    if (onUploadClick) {
      onUploadClick();
    }
    if (currentImageUrl) {
      setUploadedImageUrl(null);
    }
    if (widgetRef.current) {
      widgetRef.current.open();
    } else {
      setTimeout(() => {
        if (widgetRef.current) {
          widgetRef.current.open();
        }
      }, 100);
    }
  }, [onUploadClick, currentImageUrl]);
  const getButtonText = useCallback(() => {
    if (isUploading || isProcessing) {
      return processingText;
    }
    if (currentImageUrl) {
      return 'Edit';
    }
    return 'Add';
  }, [isUploading, isProcessing, currentImageUrl, processingText]);
  const isButtonDisabled = isUploading || isProcessing;
  const renderProps: PhotoUploadRenderProps = React.useMemo(() => ({
    currentImageUrl: currentImageUrl || null,
    isButtonDisabled,
    buttonText: getButtonText(),
    handleUploadClick,
    discardStagedUpload,
    hasImage
  }), [currentImageUrl, isButtonDisabled, handleUploadClick, discardStagedUpload, hasImage, getButtonText]);
  return <>
      <UploadWidget onLoad={handleWidgetLoad} onResult={handleUploadResult} onError={handleUploadError} />
      {typeof children === 'function' ? children(renderProps) : children}
    </>;
};